import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDialog, Button, Banner, Tag, Icon, Dialog } from '@workspace/design-system/src/components';
import { Channel } from '@src/models/channel';
import { PlanOrderDetailModal } from './plan-order-detail-modal';
import type { PaymentRequestFormData } from '../payment-request/payment-request-form-modal';
import { PlanNoPaymentFormModal } from '../plan-no-payment/plan-no-payment-form-modal';
import type { PlanNoPaymentFormData } from '../plan-no-payment/plan-no-payment-form-modal';
import { ApplyPlanNoCustomerDialog } from './apply-plan-no-customer-dialog';
import { PlanActionDialog } from '../components/plan-action-dialog';
import { PaymentCancelTossDialog } from './payment-cancel-toss-dialog';
import { CustomerInfoEditModal } from './customer-info-edit-modal';
import { PaymentRequestFormModal } from '../payment-request/payment-request-form-modal';
import { CancelPaymentRequestDialog } from './cancel-payment-request-dialog';
import { ApplyPlanDirectPaymentDialog } from './apply-plan-direct-payment-dialog';
import { PaymentCancelFormDialog } from './payment-cancel-form-dialog';
import { PlanCancelUnavailableDialog } from './plan-cancel-unavailable-dialog';
import { PlanCancelEventDialog } from './plan-cancel-event-dialog';
import type { PlanCancelDialogOption } from './plan-cancel-event-dialog';
import type { CancelablePlanEventResponseModel } from '@uniquegood/realworld-admin-interface';
import { useCurrentPaymentRequest, usePlanHistory, useChannelDetail, useCurrentApplicationPlan } from '@src/hooks/channel/queries';
import { useChannelContact } from '@src/hooks/channel/queries';
import { useChannelPagination } from '@src/hooks/channel/use-channel-pagination';
import { useApplyPlan, useCancelPaymentRequest, useApplyPlanByPaymentRequest, useUpdatePaymentRequestLink } from '@src/hooks/channel/mutations';
import type { PlanHistoryResponseModel, PaymentRequestSummaryResponseModel, CurrentApplicationPlanResponseModel, PlanType as ApiPlanType, ApplicationPlanEventType } from '@uniquegood/realworld-admin-interface';
import { getPlanLabel, PLAN_TYPE_LABELS } from '../constants';
import { Tooltip, useToast } from '@workspace/design-system/src/components';
import { RW_PAYMENTS_HOST } from '@src/config';
import type { PaymentChannel } from '@src/apis/pay';
import { planApi } from '@src/apis/admin';



interface ChannelPlanTabProps {
  channel: Channel;
  onApplyPlan?: () => void;
}

export function ChannelPlanTab({ channel, onApplyPlan }: ChannelPlanTabProps) {
  const { openDialog, closeDialog } = useDialog();
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  
  // 중첩된 다이얼로그를 위한 별도의 dialog hook
  const { openDialog: openInnerDialog, closeDialog: closeInnerDialog } = useDialog();

  // 새로고침 제한 (2초)
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCancellingPlan, setIsCancellingPlan] = useState(false);

  // Mutation hooks
  const applyPlanMutation = useApplyPlan();
  const cancelPaymentRequestMutation = useCancelPaymentRequest();
  const applyPlanByPaymentRequestMutation = useApplyPlanByPaymentRequest();
  const updatePaymentRequestLinkMutation = useUpdatePaymentRequestLink();

  // 페이지네이션
  const pagination = useChannelPagination({
    initialPage: 1,
    initialLimit: 10,
    initialTotal: 0,
  });

  // API 호출
  const { data: contactData, refetch: refetchContactData } = useChannelContact(channel.channelId);
  const { data: channelDetail, refetch: refetchChannelDetail } = useChannelDetail(channel.channelId);
  const { data: currentPaymentRequest, refetch: refetchPaymentRequest } = useCurrentPaymentRequest(channel.channelId);
  const { data: currentApplicationPlan, refetch: refetchCurrentApplicationPlan } = useCurrentApplicationPlan(channel.channelId);
  const { data: planHistoryData, isLoading: isLoadingPlanHistory, refetch: refetchPlanHistory } = usePlanHistory(
    channel.channelId,
    {
      page: pagination.currentPage,
      limit: pagination.limit,
    }
  );

  // 페이지네이션 total 업데이트
  useEffect(() => {
    if (planHistoryData) {
      pagination.setTotal(planHistoryData.total);
    }
  }, [planHistoryData]);

  // 고객 정보가 있는지 확인
  const hasCustomerInfo = Boolean(
    contactData?.customerName ||
    contactData?.contactName ||
    contactData?.contactEmail ||
    contactData?.contactPhone
  );

  // 실제 사용할 채널
  const effectiveChannel = useMemo(() => {
    let result = { ...channel };
    if (channelDetail) {
      result = {
        ...result,
        ...channelDetail,
        channelName: channelDetail.channelName || channel.channelName
      };
    }
    if (contactData) {
      // contactData는 고객 정보만 포함하므로, 고객 정보 필드만 선택적으로 병합
      // 채널 정보 필드(channelId, channelName 등)는 덮어쓰지 않음
      result = {
        ...result,
        customerName: contactData.customerName ?? result.customerName,
        businessType: contactData.businessType ?? result.businessType,
        contactName: contactData.contactName ?? result.contactName,
        contactTitle: contactData.contactTitle ?? result.contactTitle,
        contactEmail: contactData.contactEmail ?? result.contactEmail,
        contactPhone: contactData.contactPhone ?? result.contactPhone,
        memo: contactData.memo ?? result.memo,
      };
    }
    return result;
  }, [channel, channelDetail, contactData]);

  // 요금제 표시 텍스트
  const getPlanDisplayName = () => {
    return getPlanLabel(effectiveChannel.plan);
  };

  // 요금제가 무료인지 확인
  const isFreePlan = !effectiveChannel.plan || effectiveChannel.plan === 'free';

  // 사용중인 요금제 API 리프래시 함수
  const handleRefreshPlanData = useCallback(async () => {
    // 사용중인 요금제 관련 쿼리들을 리프래시
    await Promise.all([
      refetchChannelDetail(),
      refetchContactData(),
      refetchPlanHistory(),
      refetchPaymentRequest(),
      refetchCurrentApplicationPlan(),
    ]);
    // 쿼리 캐시도 무효화하여 최신 데이터 보장
    queryClient.invalidateQueries({ queryKey: ['channel-detail', channel.channelId] });
    queryClient.invalidateQueries({ queryKey: ['channel-contact', channel.channelId] });
    queryClient.invalidateQueries({ queryKey: ['plan-history', channel.channelId] });
    queryClient.invalidateQueries({ queryKey: ['current-payment-request', channel.channelId] });
    queryClient.invalidateQueries({ queryKey: ['current-application-plan', channel.channelId] });
  }, [refetchChannelDetail, refetchContactData, refetchPlanHistory, refetchPaymentRequest, refetchCurrentApplicationPlan, queryClient, channel.channelId]);

  // 유료 요금제 적용 핸들러
  const handleApplyPlanClick = () => {
    const showApplyPlanDialog = (channelToUse: Channel = effectiveChannel) => {
      openDialog(
        <PlanActionDialog
          mode="apply"
          channelName={channelToUse.channelName}
          onClose={closeDialog}
          onActionWithoutPayment={() => {
            closeDialog();
            // 결제 없이 적용 모달 표시
            const mode = 'apply' as const;
            openDialog(
              <PlanNoPaymentFormModal
                channel={channelToUse}
                mode={mode}
                onClose={closeDialog}
                onRefresh={handleRefreshPlanData}
                onSubmit={async (formData: PlanNoPaymentFormData) => {
                  try {
                    await applyPlanMutation.mutateAsync({
                      channelId: channelToUse.channelId,
                      formData,
                      mode,
                    });
                    const planName = getPlanLabel(formData.planType);
                    const actionText: '적용' | '연장' = (mode as 'apply' | 'extend') === 'extend' ? '연장' : '적용';
                    openToast({
                      type: 'confirm',
                      message: `'${channelToUse.channelName}' 채널에 ${planName} 요금제 ${actionText}이 완료되었습니다.`,
                    });
                  } catch (error: any) {
                    openToast({
                      type: 'destructive',
                      message: '요금제 적용에 실패했어요. 다시 시도해 주세요.',
                    });
                    throw error; // onRefresh가 호출되지 않도록 에러를 다시 throw
                  }
                }}
              />
            );
          }}
          onConfirm={() => {
            closeDialog();
            // 결제 요청 모달 표시
            openDialog(
              <PaymentRequestFormModal
                channel={channelToUse}
                mode="apply"
                onClose={closeDialog}
                onSubmit={async (formData: PaymentRequestFormData) => {
                  // 기존 콜백 호출 (필요한 경우)
                  if (onApplyPlan) {
                    // onApplyPlan은 별도로 처리
                  }
                }}
                onRequestTossPG={async (formData: PaymentRequestFormData) => {
                  // 기존 콜백 호출 (필요한 경우)
                  if (onApplyPlan) {
                    // onApplyPlan은 별도로 처리
                  }
                }}
                onSuccess={(channel) => {
                 
                 
                }}
              />
            );
          }}
        />
      );
    };

    if (!hasCustomerInfo) {
      // 고객 정보가 없는 경우
      openDialog(
        <ApplyPlanNoCustomerDialog
          onClose={closeDialog}
          onConfirm={() => {
            closeDialog();
            // 고객 정보 입력 모달 열기
            openDialog(
              <CustomerInfoEditModal
                channel={effectiveChannel}
                mode="required"
                onlyEdit={false}
                onClose={closeDialog}
                onComplete={async (updatedChannel) => {
                  closeDialog();
                  // 고객 정보 쿼리 refetch하여 동기화 보장
                  await refetchContactData();
                  // 고객 정보 입력 완료 후 PlanActionDialog 표시 (업데이트된 채널 정보 사용)
                  showApplyPlanDialog(updatedChannel);
                }}
              />
            );
          }}
        />
      );
    } else {
      // 고객 정보가 있는 경우
      showApplyPlanDialog();
    }
  };

  // 리얼월드 요금제 적용 핸들러
  const handleApplyRealworldPlanClick = () => {
    openDialog(
      <ApplyRealworldPlanDialog
        channelName={effectiveChannel.channelName}
        channelId={effectiveChannel.channelId}
        onRefresh={handleRefreshPlanData}
      />,
    );
  };

  // 취소 가능 이벤트를 다이얼로그 옵션으로 변환
  const convertToDialogOptions = (events: CancelablePlanEventResponseModel[]): PlanCancelDialogOption[] => {
    return events.map((event, index) => {
      // eventType 한글 변환
      const eventTypeLabel = event.eventType === 'Applied' ? '적용' :
        event.eventType === 'Extended' ? '연장' :
        event.eventType === 'Renewed' ? '갱신' : event.eventType || '-';

      // paymentChannel 한글 변환
      const paymentChannelLabel = event.paymentChannel === 'TossPG' ? '토스 PG' :
        event.paymentChannel === 'Invoice' ? '직접 결제' :
        event.paymentChannel === 'Redpoint' ? '레드포인트' :
        event.paymentChannel === null || event.paymentChannel === undefined ? '결제 없음' : '-';

      // 가격 포맷팅
      const priceLabel = event.price ? `${event.price.toLocaleString('ko-KR')}원` : '-';

      // eventId 앞 12자리 + ...
      const eventIdShort = event.eventId ? `${event.eventId.substring(0, 12)}...` : '-';

      // 라벨 생성: "eventId... / 개월 / 이벤트타입 / 결제방식 / 가격"
      const label = `${eventIdShort} / ${event.useMonths || 0}개월 / ${eventTypeLabel} / ${paymentChannelLabel} / ${priceLabel}`;

      return {
        id: event.eventId || '',
        label,
        disabled: index !== 0, // 첫 번째만 활성화
        data: event as unknown as Record<string, unknown>,
      };
    });
  };

interface ApplyRealworldPlanDialogProps {
  channelName: string;
  channelId: string;
  onRefresh: () => Promise<void>;
}

function ApplyRealworldPlanDialog({ channelName, channelId, onRefresh }: ApplyRealworldPlanDialogProps) {
  const { closeDialog } = useDialog();
  const { openToast } = useToast();
  const [isApplying, setIsApplying] = useState(false);

  const handleApply = async () => {
    if (isApplying) return;
    setIsApplying(true);
    try {

      await planApi.applyRealworldPlan(channelId, '1.0');
      closeDialog();
      openToast({
        type: 'confirm',
        message: `'${channelName}' 채널에 리얼월드 요금제가 적용되었습니다.`,
      });
      // 사용중인 요금제 리프래시
      await onRefresh();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || '리얼월드 요금제 적용에 실패했습니다.';
      openToast({
        type: 'destructive',
        message: errorMessage,
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog>
      <Dialog.Content>
        <Dialog.Header
          title={`${channelName} 채널에\n리얼월드 요금제를 적용하시겠습니까?`}
          type="question"
          showIcon
        />
        <Dialog.Body>
          <div className="flex flex-col gap-2">
            <p className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">
              • 사용가능한 액션에 &apos;추가기능(다기능 프레임 사진 촬영, [카메라] 스크레지 이미지 비교 등)&apos;
              설정가능
            </p>
            <p className="text-[14px] text-[#595959] leading-[20px] tracking-[-0.1px]">
              • 콘텐츠 유료 판매 가능
            </p>
          </div>
        </Dialog.Body>

        <Dialog.Footer>
          <div className="flex gap-2">
            <Button styleType="basic" styleSize="M-40" onClick={closeDialog}>
              닫기
            </Button>
            <Button
              styleType="primary"
              styleSize="M-40"
              isLoading={isApplying}
              onClick={handleApply}
            >
              적용하기
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

  // 요금제 취소 핸들러
  const handleCancelClick = async () => {
    setIsCancellingPlan(true);

    try {
      // 취소 가능한 이벤트 조회
      const response = await planApi.getCancelablePlanEvents(channel.channelId);
      const cancelableEvents: CancelablePlanEventResponseModel[] = response.data?.data ?? [];

      // 취소 가능한 이벤트가 없으면 취소 불가 다이얼로그 표시
      if (cancelableEvents.length === 0) {
        openDialog(
          <PlanCancelUnavailableDialog onClose={closeDialog} />
        );
        return;
      }
      
      // 현재 요금제의 만료일에서 가장 최근 추가한 요금제 개월수를 뺀 날짜 계산
      const endDate = new Date(currentPlan?.currentPeriodEndAt || '');
      const months = cancelableEvents[0].useMonths || 0;
      
      // 테스트 모드: 1개월 = 30분으로 치환
      const TEST_MODE = false; // 테스트 완료 후 false로 변경
      let cancelExpiredDate: Date;
      
      if (TEST_MODE) {
        // 테스트 모드: 개월수를 분으로 변환 (1개월 = 30분)
        cancelExpiredDate = new Date(endDate);
        cancelExpiredDate.setMinutes(cancelExpiredDate.getMinutes() - (months * 30));
      } else {
        // 프로덕션 모드: 기존 로직 유지
        const getLastDay = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
        const isLastDayOfMonth = endDate.getDate() === getLastDay(endDate.getFullYear(), endDate.getMonth());
        const originalDay = endDate.getDate();
        const originalMonth = endDate.getMonth();
        const originalYear = endDate.getFullYear();
        
        // 목표 월 계산
        let targetYear = originalYear;
        let targetMonth = originalMonth - months;
        while (targetMonth < 0) {
          targetMonth += 12;
          targetYear -= 1;
        }
        
        cancelExpiredDate = new Date(endDate);
        cancelExpiredDate.setMonth(originalMonth - months);
        
        // 만료일이 말일이었으면, 뺀 개월 수의 말일로 설정
        // 말일이 아니었어도 해당 월에 그 날짜가 없어서 날짜가 조정되었으면 목표 월의 말일로 조정
        const wasDateAdjusted = cancelExpiredDate.getDate() !== originalDay || cancelExpiredDate.getMonth() !== targetMonth;
        if (isLastDayOfMonth || wasDateAdjusted) {
          cancelExpiredDate.setFullYear(targetYear);
          cancelExpiredDate.setMonth(targetMonth);
          cancelExpiredDate.setDate(getLastDay(targetYear, targetMonth));
        }
      }
      
      // 취소 가능한 요금제중 최상위 요금제를 취소할 경우 요금제가 끝났는지 확인
      const planEnded = cancelExpiredDate < new Date();

      // 첫 번째 이벤트의 paymentChannel로 케이스 판단
      const firstEvent = cancelableEvents[0];
      const paymentChannel = firstEvent.paymentChannel;

      // 다이얼로그 옵션 생성
      const dialogOptions = convertToDialogOptions(cancelableEvents);

      // 요금제명 가져오기
      const planName = currentPlan?.planType ? getPlanLabel(currentPlan.planType.toLowerCase() as any) : '';

      // 취소 가능한 이벤트가 1개인지 확인
      const hasOnlyOnePlan = cancelableEvents.length === 1;
      // 취소 다이얼로그 표시
      openDialog(
        <PlanCancelEventDialog
          planName={planName}
          paymentChannel={paymentChannel}
          planEnded={planEnded}
          options={dialogOptions}
          onClose={closeDialog}
          onConfirm={async ({ reason, optionId }) => {
            setIsCancellingPlan(true);
            try {
              await planApi.cancelPlanEvents(
                channel.channelId,
                undefined, // x-rwd-api-version은 default value 사용
                {
                  applicationPlanEventId: optionId,
                  memo: reason,
                }
              );
              closeDialog();
              if(hasOnlyOnePlan){
                openToast({
                  type: 'confirm',
                  message: `'${effectiveChannel.channelName}'채널에 ${planName}요금제가 취소됐어요.`,
                });
              }else{
                openToast({
                  type: 'confirm',
                  message: `${planName}요금제 연장이 취소됐어요.`,
                });
              }
              // 사용중인 요금제 갱신
              await handleRefreshPlanData();
            } catch (error: any) {
              const errorMessage = error?.response?.data?.message || error?.message || '요금제 취소 중 오류가 발생했습니다.';
              openToast({
                type: 'destructive',
                message: errorMessage,
              });
            } finally {
              setIsCancellingPlan(false);
            }
          }}
        />
      );
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || '취소 가능한 이벤트 조회 중 오류가 발생했습니다.';
      openToast({
        type: 'destructive',
        message: errorMessage,
      });
    } finally {
      setIsCancellingPlan(false);
    }
  };

  // 요금제 연장 핸들러
  const handleExtendClick = () => {
    // 먼저 확인 다이얼로그 표시
    openDialog(
      <PlanActionDialog
        mode="extend"
        channelName={effectiveChannel.channelName}
        currentPlan={getPlanDisplayName()}
        onClose={closeDialog}
        onActionWithoutPayment={() => {
          closeDialog();
          // 결제 없이 연장 모달 표시
          const mode = 'extend' as const;
          openDialog(
            <PlanNoPaymentFormModal
              channel={effectiveChannel}
              mode={mode}
              currentEndDate={currentPlan?.currentPeriodEndAt || undefined}
              onClose={closeDialog}
              onRefresh={handleRefreshPlanData}
              onSubmit={async (formData: PlanNoPaymentFormData) => {
                try {
                  await applyPlanMutation.mutateAsync({
                    channelId: effectiveChannel.channelId,
                    formData,
                    mode,
                  });
                  const planName = getPlanLabel(formData.planType || effectiveChannel.plan || '');
                  const actionText: '적용' | '연장' = (mode as 'apply' | 'extend') === 'extend' ? '연장' : '적용';
                  openToast({
                    type: 'confirm',
                    message: `'${effectiveChannel.channelName}' 채널에 ${planName} 요금제 ${actionText}이 완료되었습니다.`,
                  });
                } catch (error: any) {
                  openToast({
                    type: 'destructive',
                    message: '요금제 적용에 실패했어요. 다시 시도해 주세요.',
                  });
                  throw error; // onRefresh가 호출되지 않도록 에러를 다시 throw
                }
              }}
            />
          );
        }}
        onConfirm={() => {
          closeDialog();
          // 결제 요청 모달 표시 (PaymentRequestFormModal을 extend 모드로 사용)
          openDialog(
            <PaymentRequestFormModal
              channel={effectiveChannel}
              mode="extend"
              currentEndDate={currentPlan?.currentPeriodEndAt || undefined}
              onClose={closeDialog}
              onSubmit={async (formData: PaymentRequestFormData) => {
                // 기존 콜백 호출 (필요한 경우)
              }}
              onRequestTossPG={async (formData: PaymentRequestFormData) => {
                // 기존 콜백 호출 (필요한 경우)
              }}
              onSuccess={(channel) => {
             
              }}
            />
          );
        }}
      />
    );
  };

  // 현재 요금제 정보 (API에서 조회)
  const currentPlan: CurrentApplicationPlanResponseModel | null = currentApplicationPlan ?? null;

  // 진행 중인 결제 요청 (직접 API 응답 사용)
  const pendingPayment: PaymentRequestSummaryResponseModel | null = currentPaymentRequest ?? null;

  // 요금제 내역
  const planHistory = useMemo<PlanHistoryResponseModel[]>(() => {
    if (!planHistoryData || !planHistoryData.items || planHistoryData.items.length === 0) {
      return [];
    }
    
    return planHistoryData.items;
  }, [planHistoryData]);

  // 상세보기 버튼 클릭 핸들러 (orderId로 결제 내역 조회)
  const handleViewPaymentDetail = useCallback((orderId: string | null | undefined) => {
    if (!orderId) {
      console.warn('orderId가 없습니다.');
      return;
    }
    openDialog(
      <PlanOrderDetailModal
        applicationId={channel.channelId}
        orderId={orderId}
        onClose={closeDialog}
      />
    );
  }, [channel.channelId, openDialog, closeDialog]);

  // 스크롤 관련 상태와 refs
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }, []);

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setIsScrolled(scrollTop > 0);
      setShowBottomShadow(scrollTop + clientHeight < scrollHeight);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      // 초기 상태 확인
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // 데이터 갱신 핸들러 (2초 제한)
  const handleRefreshPaymentData = useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshTime < 2000) {
      alert('데이터 갱신은 2초에 한 번만 가능합니다.');
      return;
    }

    setIsRefreshing(true);
    setLastRefreshTime(now);

    // 사용중인 요금제, 진행중인 결제 요청, 요금제 내역 API 재조회
    Promise.all([
      refetchCurrentApplicationPlan(), // 사용중인 요금제
      refetchChannelDetail(), // 채널 상세 (요금제 정보 포함)
      refetchPaymentRequest(), // 진행중인 결제 요청
      refetchPlanHistory(), // 요금제 내역
    ]).finally(() => {
      setIsRefreshing(false);
    });
  }, [lastRefreshTime, refetchCurrentApplicationPlan, refetchChannelDetail, refetchPaymentRequest, refetchPlanHistory]);

  return (
    <div className="flex flex-col gap-12 py-4">
      {/* 사용중인 요금제 */}
      <section className="flex flex-col gap-6">
        <h2 className="text-base font-bold text-[#101010] leading-5">사용중인 요금제</h2>

        {isFreePlan ? (
          <div className="w-full outline outline-1 outline-[#f0f0f0] inline-flex flex-col justify-start items-start">
            <div className="self-stretch h-10 border-b border-[#f4f4f4] inline-flex justify-between items-center">
              <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                <div className="justify-start text-[#101010] text-sm font-bold font-['Pretendard'] leading-4 line-clamp-1">요금제</div>
              </div>
              <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                <div className="text-center justify-center text-[#101010] text-sm font-normal font-['Pretendard'] leading-5">무료</div>
                <Button
                  styleType="tonal"
                  styleSize="XS-24"
                  onClick={handleApplyPlanClick}
                  disabled={!!pendingPayment}
                >
                  유료 요금제 적용하기
                </Button>
                <Button
                  styleType="basic"
                  styleSize="XS-24"
                  onClick={handleApplyRealworldPlanClick}
                  disabled={!!pendingPayment}
                >
                  리얼월드 요금제 적용하기
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full outline outline-1 outline-[#f0f0f0] inline-flex flex-col justify-start items-start">
            {/* 요금제 */}
            <div className="self-stretch h-10 border-b border-[#f4f4f4] inline-flex justify-between items-center">
              <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                <div className="justify-start text-[#101010] text-sm font-bold font-['Pretendard'] leading-4 line-clamp-1">요금제</div>
              </div>
              <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                <div className="text-center justify-center text-[#101010] text-sm font-normal font-['Pretendard'] leading-5">
                  {currentPlan?.planType ? getPlanLabel(currentPlan.planType.toLowerCase() as any) : '-'}
                </div>
                <div className="flex justify-end items-center gap-2">
                  <Button
                    styleType="tonal-destructive"
                    styleSize="XS-24"
                    onClick={handleCancelClick}
                    disabled={!!pendingPayment}
                  >
                    사용 취소하기
                  </Button>
                  {(effectiveChannel.plan !== 'pro' && effectiveChannel.plan !== 'realworld') && (
                    <Button
                      styleType="basic"
                      styleSize="XS-24"
                      onClick={handleExtendClick}
                      disabled={!!pendingPayment}
                    >
                      연장하기
                    </Button>
                  )}
                </div>
              </div>
            </div>


            {/* 만료일 */}
            <div className="self-stretch h-10 border-b border-[#f0f0f0] inline-flex justify-between items-center">
              <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                <div className="justify-start text-[#101010] text-sm font-bold font-['Pretendard'] leading-4 line-clamp-1">만료일</div>
              </div>
              <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                <div className="text-center justify-center text-[#101010] text-sm font-normal font-['Pretendard'] leading-5">
                  {currentPlan?.currentPeriodEndAt ? new Date(currentPlan.currentPeriodEndAt).toLocaleDateString('ko-KR') : '-'}
                </div>
              </div>
            </div>

            {/* 다음 결제일 */}
            <div className="self-stretch h-10 inline-flex justify-between items-center">
              <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                <div className="justify-start text-[#101010] text-sm font-bold font-['Pretendard'] leading-4 line-clamp-1">다음 결제일</div>
              </div>
              <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                <div className={`text-center justify-center text-sm font-normal font-['Pretendard'] leading-5 ${
                  currentPlan?.nextBillingAt ? 'text-[#101010]' : 'text-[#bfbfbf]'
                }`}>
                  {currentPlan?.nextBillingAt ? new Date(currentPlan.nextBillingAt).toLocaleDateString('ko-KR') : '해당 없음'}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 진행중인 결제 요청 */}
      <section className="flex flex-col gap-6">
        <h2 className="text-base font-bold text-[#101010] leading-5">진행중인 결제 요청</h2>

        {!pendingPayment ? (
          <div className="flex items-center justify-center p-8 border border-[#F0F0F0] rounded-lg bg-[#FAFAFA]">
            <span className="text-sm text-[#8C8C8C] leading-5">진행중인 결제 요청이 없습니다.</span>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {(() => {
              const payment = pendingPayment;
              
              // 결제 방식 판단: paymentCode가 있으면 토스 PG, 없으면 직접 결제(세금계산서)
              const paymentType: 'toss-pg' | 'tax-invoice' = payment.paymentCode ? 'toss-pg' : 'tax-invoice';
              
              // 요청 타입 판단: 현재 요금제가 있으면 연장, 없으면 적용
              const requestType: 'apply' | 'extend' = isFreePlan ? 'apply' : 'extend';
              
              // 만료 여부 확인
              const isExpired = payment.paymentCode?.expiresAt 
                ? new Date(payment.paymentCode.expiresAt) < new Date()
                : false;
              
              // 금액 포맷팅
              const formattedAmount = payment.price ? `${payment.price.toLocaleString('ko-KR')}원` : '-';
              
              // 결제 링크
              const paymentLink = payment.paymentCode?.code || undefined;
              
              // 결제 링크 만료일 포맷팅
              const paymentLinkExpiresAt = payment.paymentCode?.expiresAt 
                ? new Date(payment.paymentCode.expiresAt).toLocaleString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : undefined;
              
              // 요금제 이름
              const planName = payment.productName || '';
              
              return (
                <div key={payment.id || ''} className="flex flex-col gap-4">
                  {/* 배너 */}
                  <Banner
                    type="information"
                    icon={true}
                    content={
                      paymentType === 'toss-pg'
                        ? `고객이 결제를 완료하면 요금제가 자동으로 ${requestType === 'apply' ? '적용' : '연장'}됩니다.`
                        : `입금 확인 후 요금제 ${requestType === 'apply' ? '적용' : '연장'}을 진행해 주세요.`
                    }
                  />

                  {/* 결제 정보 테이블 */}
                  <div className="w-full outline outline-1 outline-[#f0f0f0] inline-flex flex-col justify-start items-start">
                    {/* 결제 내용 */}
                    <div className="self-stretch min-h-10 border-b border-[#f4f4f4] inline-flex justify-between items-center">
                      <div data-type="title" className="w-28 h-full px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                        <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">결제 내용</div>
                      </div>
                      <div className="flex-1 self-stretch px-4 flex justify-between items-center gap-3">
                        <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                          {planName} / {payment.useMonths || 0}개월
                        </div>
                        <div className="flex justify-end items-center gap-2">
                          <Button
                            styleType="text-destructive"
                            styleSize="XS-24"
                            onClick={() => {
                              openInnerDialog(
                                <CancelPaymentRequestDialog
                                  onClose={closeInnerDialog}
                                  onConfirm={async () => {
                                      try {
                                        await cancelPaymentRequestMutation.mutateAsync({
                                          channelId: effectiveChannel.channelId,
                                          paymentRequestId: payment.id || '',
                                        });
                                        closeInnerDialog();
                                        // 모든 관련 데이터 새로고침
                                        await handleRefreshPlanData();
                                        openToast({
                                          type: 'confirm',
                                          message: '결제 요청이 취소되었습니다.',
                                        });
                                      } catch (error: any) {
                                        const errorMessage = error?.response?.data?.message || error?.message || '결제 요청 취소 중 오류가 발생했습니다.';
                                        openToast({
                                          type: 'destructive',
                                          message: errorMessage,
                                        });
                                      }
                                  }}
                                />
                              );
                            }}
                            disabled={cancelPaymentRequestMutation.isPending}
                            isLoading={cancelPaymentRequestMutation.isPending}
                          >
                            결제 요청 취소
                          </Button>
                          {paymentType === 'tax-invoice' && (
                            <Button
                              styleType="tonal"
                              styleSize="XS-24"
                              onClick={() => {
                                openInnerDialog(
                                  <ApplyPlanDirectPaymentDialog
                                    mode={requestType}
                                    planName={planName}
                                    channelName={effectiveChannel.channelName}
                                    isLoading={applyPlanByPaymentRequestMutation.isPending}
                                    onClose={closeInnerDialog}
                                    onConfirm={async (startDate) => {
                                      try {
                                        await applyPlanByPaymentRequestMutation.mutateAsync({
                                          channelId: effectiveChannel.channelId,
                                          paymentRequestId: payment.id || '',
                                          paymentAt: startDate.toISOString(),
                                        });
                                        closeInnerDialog();
                                        // 모든 관련 데이터 새로고침
                                        await handleRefreshPlanData();
                                        const actionText: '적용' | '연장' = (requestType as 'apply' | 'extend') === 'extend' ? '연장' : '적용';
                                        openToast({
                                          type: 'confirm',
                                          message: `${effectiveChannel.channelName} 채널에 ${planName} 요금제 ${actionText}이 완료되었습니다.`,
                                        });
                                      } catch (error: any) {
                                        openToast({
                                          type: 'destructive',
                                          message: '요금제 적용에 실패했어요. 다시 시도해 주세요.',
                                        });
                                      }
                                    }}
                                  />
                                );
                              }}
                              disabled={applyPlanByPaymentRequestMutation.isPending}
                              isLoading={applyPlanByPaymentRequestMutation.isPending}
                            >
                              요금제 {requestType === 'apply' ? '적용' : '연장'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 결제 금액 */}
                    <div className="self-stretch h-10 border-b border-[#f0f0f0] inline-flex justify-between items-center">
                      <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                        <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">결제 금액</div>
                      </div>
                      <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                        <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                          {formattedAmount}
                        </div>
                      </div>
                    </div>

                    {/* 토스PG: 결제 링크 */}
                    {paymentType === 'toss-pg' && paymentLink && (
                      <div className="self-stretch h-10 border-b border-[#f0f0f0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                          <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">결제 링크</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex  items-center gap-3">
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://${RW_PAYMENTS_HOST}/pay/${paymentLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-sm font-normal leading-5 underline ${isExpired ? 'text-[#8C8C8C]' : 'text-[#3C78F4] hover:text-[#2d64dc]'
                                }`}
                            >
                              {`https://${RW_PAYMENTS_HOST}/pay/${paymentLink}`}
                            </a>
                            {!isExpired ? (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(`https://${RW_PAYMENTS_HOST}/pay/${paymentLink}`);
                                  alert('결제 링크가 복사되었습니다.');
                                }}
                                className="flex items-center justify-center"
                              >
                                <Icon name="Copy" size={16} color="#8C8C8C" />
                              </button>
                            ) : (
                              <Button
                                styleType="primary"
                                styleSize="XS-24"
                                icon="Reload"
                                onClick={async () => {
                                  if (!payment.id) {
                                    openToast({
                                      type: 'destructive',
                                      message: '결제 요청 ID가 없습니다.',
                                    });
                                    return;
                                  }
                                  
                                  try {
                                    await updatePaymentRequestLinkMutation.mutateAsync({
                                      channelId: effectiveChannel.channelId,
                                      paymentRequestId: payment.id,
                                    });
                                    openToast({
                                      type: 'confirm',
                                      message: '결제 링크가 재생성되었습니다.',
                                    });
                                    // mutation의 onSuccess에서 이미 쿼리를 업데이트하므로,
                                    // 진행 중인 결제 요청 데이터가 자동으로 갱신됩니다.
                                  } catch (error: any) {
                                    const errorMessage = error?.response?.data?.message || error?.message || '결제 링크 재생성 중 오류가 발생했습니다.';
                                    openToast({
                                      type: 'destructive',
                                      message: errorMessage,
                                    });
                                  }
                                }}
                                disabled={updatePaymentRequestLinkMutation.isPending}
                                isLoading={updatePaymentRequestLinkMutation.isPending}
                              >
                                결제 링크 재생성
                              </Button>
                            )}
                          </div>
                          {paymentLinkExpiresAt && !isExpired && (
                            <div className="text-sm font-normal leading-5 text-[#FF4D4F]">
                              *결제 유효 기한: {paymentLinkExpiresAt} 까지
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 토스PG: 상태 */}
                    {paymentType === 'toss-pg' && (
                      <div className="self-stretch h-10 inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                          <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">상태</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex items-center gap-3">
                          <Tag
                            type="default"
                            color='gray'
                            size="M-24"
                            text='결제 전'
                          />
                          <button
                            onClick={handleRefreshPaymentData}
                            disabled={isRefreshing}
                            className={`flex items-center justify-center ${isRefreshing ? 'animate-spin' : ''}`}
                          >
                            <Icon name="Reload" size={20} color={isRefreshing ? "#BFBFBF" : "#8C8C8C"} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 세금계산서: 파일 */}
                    {paymentType === 'tax-invoice' && payment.invoice && (
                      <div className="self-stretch h-10 inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#f4f4f4] flex justify-start items-start gap-2">
                          <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">세금계산서</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3">
                          <a
                            href={payment.invoice.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-normal leading-5 text-[#3C78F4] hover:text-[#2d64dc] underline"
                          >
                            {payment.invoice.fileUrl.split('/').pop() || payment.invoice.fileUrl}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </section>

      {/* 요금제 내역 */}
      <section className="flex flex-col gap-6">
        <h2 className="text-base font-bold text-[#101010] leading-5">전체 내역</h2>

        <div className="relative">
          {/* 스크롤 시 상단 그림자 */}
          {isScrolled && (
            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-[#000000]/10 to-transparent z-10 pointer-events-none" />
          )}

          {/* 테이블 컨테이너 */}
          <div
            ref={contentRef}
            className="overflow-auto max-h-[500px]"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#D9D9D9 transparent' }}
          >
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="bg-[#F5F5F5]">
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">이벤트 ID</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">발행 일시</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">요금제</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">적용 기간</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">내용</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">결제 내역</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">메모</th>
                  <th className="p-3 text-left text-[14px] font-bold text-[#101010] leading-[16px]">연관 이벤트 ID</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingPlanHistory ? (
                  <tr>
                    <td colSpan={6} className="p-3 text-center text-[#BFBFBF]">
                      로딩 중...
                    </td>
                  </tr>
                ) : planHistory.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-3 text-center text-[#BFBFBF]">
                      요금제 내역이 없습니다.
                    </td>
                  </tr>
                ) : (
                  planHistory.map((item) => {
                    // 이벤트 타입에 따른 내용 변환
                    const eventType = item.eventType as ApplicationPlanEventType | string | undefined;
                    let content: '적용' | '갱신' | '갱신 실패' | '만료' | '취소' | '해지' | '연장' | '연장 취소' = '적용';
                    switch (eventType) {
                      case 'Applied':
                        content = '적용';
                        break;
                      case 'Renewed':
                        content = '갱신';
                        break;
                      case 'RenewalFailed':
                        content = '갱신 실패';
                        break;
                      case 'Expired':
                        content = '만료';
                        break;
                      case 'Canceled':
                        content = '취소';
                        break;
                      case 'CancellationRequested':
                        content = '해지';
                        break;
                      case 'Extended':
                        content = '연장';
                        break;
                      case 'ExtensionCanceled':
                        content = '연장 취소';
                        break;
                      default:
                        content = '적용';
                        break;
                    }

                    // 이벤트 타입에 따라 beforePlanType 또는 afterPlanType 선택
                    // 적용: 이후 요금제 상태 (afterPlanType)
                    // 갱신, 갱신실패, 만료, 취소, 해지, 연장: 이전 요금제 상태 (beforePlanType)
                    let planType: string | undefined;
                    if (item.eventType === 'Applied') {
                      planType = item.afterPlanType;
                    } else {
                      planType = item.beforePlanType;
                    }

                    // 요금제 타입 변환
                    const plan = getPlanLabel(planType);

                    // 적용 기간
                    const period = item.months ? `${item.months}개월` : '-';

                    // 결제 내역 표시: orderId가 있으면 상세보기, 없으면 취소 또는 -
                    let payment = '-';
                    
                    
                     if (item.orderId) {
                      payment = '상세보기';
                    }

                    // 날짜 변환
                    const date = item.createdAt ? new Date(item.createdAt) : new Date();

                    const eventId = item.eventId ? `${item.eventId.substring(0, 12)}...` : '-';
                    const relatedEventId = item.originalEventId ? `${item.originalEventId.substring(0, 12)}...` : '-';
                    return (
                      <tr key={item.eventId || ''} className="border-b border-[#F0F0F0] hover:bg-[#F5F5F5] transition-colors">
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">
                          <div className="flex items-center gap-1">
                            {eventId}
                            {eventId !== '-' && ( <Icon 
                              name="Copy" 
                              size={16} 
                              color="#8C8C8C"
                              className="cursor-pointer hover:opacity-70 transition-opacity"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (item.eventId) {
                                  navigator.clipboard.writeText(item.eventId);
                                }
                              }}
                            />)}
                          </div>
                        </td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">{formatDate(date)}</td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">{plan}</td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">{period}</td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">{content}</td>
                        <td className="p-3 align-top">
                          {payment === '상세보기' && item.orderId ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleViewPaymentDetail(item.orderId);
                              }}
                              className="text-[14px] text-[#3C78F4] font-bold underline leading-[20px] tracking-[-0.1px] hover:text-[#2d64dc] transition-colors cursor-pointer"
                            >
                              {payment}
                            </button>
                          ) : (
                            <span className="text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px]">{payment}</span>
                          )}
                        </td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] whitespace-pre-wrap break-words align-top max-w-xs">

                        <Tooltip content={item.memo || '-'} contentOptions={{ style: { maxWidth: 320 } }}>
                            <span>
                              {(item.memo ?? '').length > 10
                                ? `${(item.memo ?? '').slice(0, 10)}...`
                                : item.memo}
                            </span>
                          </Tooltip>

                        </td>
                        <td className="p-3 text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] align-top">
                          <div className="flex items-center gap-1">
                            {relatedEventId}
                            {relatedEventId !== '-' && (
                              <Icon 
                              name="Copy" 
                              size={16} 
                              color="#8C8C8C"
                                className="cursor-pointer hover:opacity-70 transition-opacity"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  if (item.originalEventId) {
                                    navigator.clipboard.writeText(item.originalEventId);
                                  }
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* 스크롤 시 하단 그라데이션 그림자 */}
          {showBottomShadow && (
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-10 pointer-events-none" />
          )}
        </div>

        {/* 페이지네이션 */}
        {!isLoadingPlanHistory && planHistory.length > 0 && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6 py-4">
            <button
              onClick={pagination.handlePrevPage}
              disabled={pagination.currentPage === 1}
              className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeft" size={16} color={pagination.currentPage === 1 ? '#BFBFBF' : '#595959'} />
            </button>

            {(() => {
              const pages: (number | string)[] = [];
              const siblingCount = 2;
              const totalPageNumbers = siblingCount * 2 + 1;

              if (pagination.totalPages <= totalPageNumbers + 2) {
                for (let i = 1; i <= pagination.totalPages; i++) {
                  pages.push(i);
                }
              } else {
                pages.push(1);

                let startPage = Math.max(2, pagination.currentPage - siblingCount);
                let endPage = Math.min(pagination.totalPages - 1, pagination.currentPage + siblingCount);

                if (pagination.currentPage <= siblingCount + 1) {
                  endPage = Math.min(totalPageNumbers, pagination.totalPages - 1);
                }

                if (pagination.currentPage >= pagination.totalPages - siblingCount) {
                  startPage = Math.max(2, pagination.totalPages - totalPageNumbers);
                }

                const showLeftEllipsis = startPage > 2;
                if (showLeftEllipsis) {
                  pages.push('left-ellipsis');
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }

                const showRightEllipsis = endPage < pagination.totalPages - 1;
                if (showRightEllipsis) {
                  pages.push('right-ellipsis');
                }

                pages.push(pagination.totalPages);
              }

              return pages.map((page) => {
                if (typeof page === 'string') {
                  return (
                    <div key={page} className="flex items-center justify-center w-6 h-6">
                      <Icon name="Meatball" size={24} color="#595959" />
                    </div>
                  );
                }

                const isActive = page === pagination.currentPage;
                return (
                  <button
                    key={page}
                    onClick={() => pagination.goToPage(page)}
                    className={`w-6 h-6 rounded text-[14px] font-bold leading-[16px] ${isActive ? 'bg-[#E6F4FF] text-[#3C78F4]' : 'text-[#595959] hover:text-[#101010]'
                      } transition-colors`}
                  >
                    {page}
                  </button>
                );
              });
            })()}

            <button
              onClick={pagination.handleNextPage}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-2 text-[#595959] hover:text-[#101010] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon
                name="ChevronRight"
                size={16}
                color={pagination.currentPage === pagination.totalPages ? '#BFBFBF' : '#595959'}
              />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
